/**
 * Guest Route Component
 * Guards routes that should only be accessible to unauthenticated users
 *
 * Features:
 * - Redirects authenticated users away from auth pages (login, register, etc.)
 * - Shows loading state during auth initialization
 * - Respects the intended destination from location state
 */

import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/auth.store';
import { PageLoader } from '@/components/common/PageLoader';
import { PROTECTED_ROUTES } from './routes';

export interface GuestRouteProps {
  /** Child components to render when not authenticated */
  children: React.ReactNode;
  /** Custom redirect path for authenticated users (defaults to dashboard) */
  redirectTo?: string;
}

/**
 * GuestRoute - Guards routes that should only be for guests
 *
 * Shows a loading state while auth is initializing, then either:
 * - Renders children if user is NOT authenticated
 * - Redirects to dashboard (or intended destination) if user IS authenticated
 *
 * This prevents authenticated users from accessing login/register pages.
 *
 * @example
 * ```tsx
 * <GuestRoute>
 *   <LoginPage />
 * </GuestRoute>
 * ```
 */
export const GuestRoute: React.FC<GuestRouteProps> = ({
  children,
  redirectTo = PROTECTED_ROUTES.DASHBOARD,
}) => {
  const { isAuthenticated, isInitialized, isLoading } = useAuthStore();
  const location = useLocation();

  // Show loading state while auth is initializing
  if (!isInitialized || isLoading) {
    return <PageLoader />;
  }

  // If authenticated, redirect to intended destination or default
  if (isAuthenticated) {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.has('gateway_state')) {
      return <>{children}</>;
    }

    // Check if there's an intended destination from a previous redirect
    // Preserve search (query string) and hash fragments from the original location
    const from = (location.state as { from?: { pathname: string; search?: string; hash?: string } })
      ?.from;
    const destination = from
      ? `${from.pathname}${from.search || ''}${from.hash || ''}`
      : redirectTo;

    return <Navigate to={destination} replace />;
  }

  // Render guest content (login, register, etc.)
  return <>{children}</>;
};
